/**
 * roles.js — client mirror of the access model in server/roles.js.
 *
 * A role is a STARTING TEMPLATE: it gives every AREA a level - 'hidden' |
 * 'view' | 'edit' (edit includes delete). A manager of access can override
 * any area for one person (Team page, Access panel). The person's EFFECTIVE
 * access comes from the server (GET /api/users/me, kept in AppContext as
 * myAccess and read through hooks/useMyAccess); until it arrives - and in
 * tests that only know a role - the role's own levels stand in.
 *
 * Drives sidebar tabs, the view-router redirect, and whether edit/delete
 * controls render. The SERVER is the real boundary (the area gate); this just
 * avoids showing a person things the API would refuse.
 *
 * Every helper takes either a role string or an access object
 * ({ role, areas, noDelete, canManageAccess }).
 */

// Internal keys are unchanged; only what people read changed (10/5/26).
export const ROLE_LABELS = {
  admin: 'Administrator',
  operations: 'Operations',
  manager: 'Management',
  'engineer-sales': 'Sales',
  'crm-staff': 'Staffing',
  engineer: 'Field Engineer',
  pending: 'No access yet',
};
// What the Team page's role dropdown offers, most access first.
export const ROLE_OPTIONS = ['admin', 'operations', 'manager', 'engineer-sales', 'crm-staff', 'engineer'];

// (the retired sales / project-coordinator are rewritten at startup, server/schema_access.sql)
export const roleLabel = (role) => ROLE_LABELS[role] || 'No access yet';

export const LEVELS = ['hidden', 'view', 'edit'];
export const LEVEL_LABELS = { hidden: 'Hidden', view: 'View', edit: 'Edit' };
const RANK = { hidden: 0, view: 1, edit: 2 };

// Every area and the tabs it holds, in the order the Team page lists them.
// Keys and order match AREAS in server/roles.js (server/test_access.js).
export const AREAS = [
  { key: 'dashboard', label: 'Dashboard', tabs: ['dashboard'], levels: ['hidden', 'view'] },
  // the Dashboard's last-week boxes: View sees them, Edit types a week's figures
  { key: 'week-boxes', label: "Dashboard: Quoted / PO's / Invoices boxes (Edit corrects the numbers)", tabs: [] },
  { key: 'week-collected', label: 'Dashboard: $ collected box (Edit enters the amount)', tabs: [] },
  { key: 'opportunities', label: 'Opportunities', tabs: ['opportunities'] },
  { key: 'projects', label: 'Projects', tabs: ['projects'] },
  { key: 'engineers', label: 'Engineers', tabs: ['engineers'] },
  { key: 'spare-parts', label: 'Spare Parts', tabs: ['spare-parts'] },
  { key: 'accounts', label: 'Accounts & Leads', tabs: ['crm', 'leads', 'leads-dashboard'] },
  { key: 'tasks', label: 'Tasks', tabs: ['tasks'] },
  { key: 'reporting', label: 'Reporting', tabs: ['reporting'] },
  // the invoice ledger, payments and receivables (10/11/26)
  { key: 'finance-invoices', label: 'Finance Invoice', tabs: ['finance-invoices'] },
  { key: 'checklists', label: 'Checklists', tabs: ['checklists'] },
  // no tab of its own: View builds quotes from an opportunity, Edit also sees
  // and changes the rates (Engineers tab > Quote Rates)
  { key: 'quotes', label: 'Quotes (Edit sees the rates)', tabs: [] },
  // pay, burden rates and COGS (10/12/26): Engineers > Staff cost rates, the
  // cost side of quotes and projects, the COGS roll-up. Admin's alone by default.
  { key: 'cost-rates', label: 'Staff cost rates & COGS', tabs: [] },
  { key: 'ksa', label: 'GLOBAL KSA', tabs: ['ksa-overview'] },
  { key: 'staffing', label: 'Staffing CRM', tabs: ['crm-dashboard', 'crm-accounts', 'crm-contacts', 'crm-leads'] },
  { key: 'activity', label: 'Activity log', tabs: ['activity'], levels: ['hidden', 'view'] },
  // the tickets people send with "Submit a ticket" (10/23/26), admin's by default
  { key: 'tickets', label: 'Tickets', tabs: ['tickets'] },
  // AI features still being built (11/2/26): no tab of its own; hidden for every
  // role, Sharif (server bootstrapAccess) sees them until he releases them
  { key: 'ai-preview', label: 'AI features (preview)', tabs: [], levels: ['hidden', 'view'] },
  { key: 'team', label: 'Team', tabs: ['team'] },
  { key: 'my-work', label: 'My Schedule, My Project, Daily Reports', tabs: ['my-schedule', 'my-project', 'daily-reports'] },
];
const AREA_KEYS = AREAS.map((a) => a.key);
const TAB_AREA = Object.fromEntries(AREAS.flatMap((a) => a.tabs.map((t) => [t, a.key])));

// Where each role lands: the first of these it can see. Pipeline roles land on
// the Dashboard, engineers on My Schedule, staffing on the CRM Dashboard.
const TAB_ORDER = [
  'dashboard',
  'projects',
  'engineers',
  'spare-parts',
  'opportunities',
  'crm',
  'leads',
  'reporting',
  'my-schedule',
  'my-project',
  'daily-reports',
  'checklists',
  'crm-dashboard',
  'crm-accounts',
  'crm-contacts',
  'crm-leads',
  'leads-dashboard',
  'tasks',
  'finance-invoices',
  'ksa-overview',
  'team',
  'activity',
  'tickets',
];

// Role defaults - mirror of ROLE_AREAS in server/roles.js, pinned against it by
// src/data/__tests__/roleAreas.json (both sides' tests read that file).
const allAt = (level) => Object.fromEntries(AREA_KEYS.map((k) => [k, level]));
export const ROLE_AREAS = {
  // Quotes is View for every role: the rates (Quotes Edit) are only for the
  // people Sharif picks, and him (server bootstrapAccess)
  // Finance Invoice: Operations only for now, and the bootstrap admin (server)
  admin: {
    ...allAt('edit'),
    dashboard: 'view',
    activity: 'view',
    'my-work': 'hidden',
    quotes: 'view',
    'finance-invoices': 'hidden',
    'ai-preview': 'hidden',
  },
  operations: {
    ...allAt('edit'),
    dashboard: 'view',
    activity: 'hidden',
    team: 'hidden',
    'my-work': 'hidden',
    quotes: 'view',
    'week-boxes': 'view',
    'week-collected': 'hidden',
    'cost-rates': 'hidden',
    tickets: 'hidden',
    'ai-preview': 'hidden',
  },
  manager: {
    ...allAt('hidden'),
    dashboard: 'view',
    opportunities: 'edit',
    projects: 'edit',
    engineers: 'edit',
    accounts: 'edit',
    tasks: 'edit',
    reporting: 'view',
    checklists: 'edit',
    quotes: 'view',
    'spare-parts': 'edit',
    'week-boxes': 'view',
  },
  'engineer-sales': {
    ...allAt('hidden'),
    dashboard: 'view',
    opportunities: 'edit',
    projects: 'view',
    accounts: 'edit',
    tasks: 'edit',
    reporting: 'view',
    quotes: 'view',
    'spare-parts': 'view',
    'week-boxes': 'view',
  },
  'crm-staff': { ...allAt('hidden'), staffing: 'edit' },
  engineer: { ...allAt('hidden'), 'my-work': 'edit', checklists: 'edit' },
  pending: allAt('hidden'),
};
// crm-staff adds and edits staffing records but does not delete them - while
// the level is the role's own (an Edit override includes delete).
const ROLE_NO_DELETE = { 'crm-staff': ['staffing'] };

/** The same computation as effectiveAccess in server/lib/access.js. */
export function effectiveAccess(role, overrides = {}, { canManageAccess = false } = {}) {
  const known = ROLE_AREAS[role] ? role : 'pending';
  const areas = {};
  const fromRole = {};
  for (const a of AREAS) {
    const o = overrides && overrides[a.key];
    const own = known !== 'pending' && (a.levels || LEVELS).includes(o);
    areas[a.key] = own ? o : ROLE_AREAS[known][a.key];
    if (!own) fromRole[a.key] = true;
  }
  if (canManageAccess && known !== 'pending' && areas.team === 'hidden') {
    areas.team = 'view';
    delete fromRole.team;
  }
  const noDelete = (ROLE_NO_DELETE[known] || []).filter((a) => fromRole[a]);
  return { role: known, areas, fromRole, noDelete, canManageAccess: !!canManageAccess };
}

/** A role string or an access object -> an access object. */
const toAccess = (x) => (x && typeof x === 'object' && x.areas ? x : effectiveAccess(x));

export const levelOf = (x, area) => toAccess(x).areas[area] || 'hidden';
export const hasArea = (x, area, need = 'view') => RANK[levelOf(x, area)] >= RANK[need];

export const canSeeTab = (x, tabId) => {
  const area = TAB_AREA[tabId];
  return !!area && hasArea(x, area, 'view');
};

// The data domains the screens ask about, and the area each belongs to.
const DOMAIN_AREA = { 'engineer-self': 'my-work' };

export const canEdit = (x, domain) => hasArea(x, DOMAIN_AREA[domain] || domain, 'edit');

export const canDelete = (x, domain) => {
  const area = DOMAIN_AREA[domain] || domain;
  return canEdit(x, domain) && !(toAccess(x).noDelete || []).includes(area);
};

export const firstTab = (x) => TAB_ORDER.find((t) => canSeeTab(x, t)) || null;

/**
 * The view router's rule: where to send someone whose current tab they may
 * not see (a stale deep link, or a tab that just became Hidden), or null to
 * stay. Null too when they can see nothing at all - App.jsx shows its
 * no-access card then.
 */
export const redirectFor = (x, active) => {
  if (canSeeTab(x, active)) return null;
  const dest = firstTab(x);
  return dest && dest !== active ? dest : null;
};
